import styles from './NewAgentTaskForm.module.css'
import {clsx} from 'clsx'
import {useCallback, useMemo, type RefObject} from 'react'
import {SkeletonBox} from '@primer/react'
import {CopilotChatProvider} from '@github-ui/copilot-chat/CopilotChatContext'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import type {Repository} from '@github-ui/current-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useCopilotChatPayloadQuery} from '@github-ui/global-copilot-menu/hooks/useCopilotChatPayloadQuery'
import {
  NewCopilotComposer,
  type NewSessionConfiguration,
  type NewSessionInitialInput,
} from '@github-ui/one-copilot-composer/new'
import type {EnvironmentSubmit} from '@github-ui/one-copilot-composer/use-environment-selection'
import type {HandleUserSubmit} from '@github-ui/one-copilot-composer/user-submit'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import {RewrittenNewSessionComposer} from '@github-ui/agent-sessions/components/new-task-chat-input/RewrittenNewSessionComposer'
import {useNewSessionModeControls} from '@github-ui/agent-sessions/hooks/use-composer-mode-controls'
import {buildCreateTaskParams} from './build-create-task-params'

export type NewAgentSessionFormProps = {
  /** Every session started here targets this repository; only its branch can change. */
  repository: Repository
  createTask: (task: CreateAPITaskParams) => Promise<void> | void
  /** Starts the session in Sandbox or a user environment. The environment picker is offered only with it. */
  createEnvironmentTask?: EnvironmentSubmit
  onCreate?: () => void
  eventType: string
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  className?: string
  initialText?: string
  /** Maximum height for the textarea input in pixels */
  maxTextareaHeight?: number
  /** When false, cloud tasks default to the 'fix' agent and create a pull request. */
  taskModeEnabled?: boolean
  directTurnTelemetrySurface?: NewSessionConfiguration['directTurnTelemetrySurface']
}

/**
 * Starts an agent session in one repository with the same shared composer as
 * `/copilot`. The repository is locked, so Chat is not offered.
 */
export function NewAgentSessionForm({
  repository,
  createTask,
  createEnvironmentTask,
  onCreate,
  eventType,
  textAreaRef,
  className,
  initialText,
  maxTextareaHeight,
  taskModeEnabled,
  directTurnTelemetrySurface,
}: NewAgentSessionFormProps) {
  const copilotChatPayload = useCopilotChatPayloadQuery()
  const modeControls = useNewSessionModeControls()

  const handleUserSubmit = useCallback<HandleUserSubmit>(
    async (...submission) => {
      const task = buildCreateTaskParams({eventType, taskModeEnabled}, ...submission)
      if (!task) return
      await createTask(task)
      onCreate?.()
    },
    [createTask, eventType, onCreate, taskModeEnabled],
  )

  const initialInput = useMemo<NewSessionInitialInput>(
    () => ({
      target: {kind: 'task', repository: {kind: 'repository', repository}, lockRepository: true},
      // Matches a repository launch on `/copilot`: keep the user's last environment.
      restoreLastEnvironment: true,
      behavior: 'prefill',
      ...(initialText ? {prompt: {text: initialText}} : {}),
    }),
    [initialText, repository],
  )

  const configuration = useMemo<NewSessionConfiguration>(
    () => ({
      // The locked repository's own policy decides Sandbox once it resolves;
      // this only keeps a restored Sandbox selection from being dropped before then.
      supportsManagedSandbox: true,
      currentUserLogin: copilotChatPayload?.currentUserLogin,
      agentQuota: copilotChatPayload?.agentQuotaSnapshot,
      mcpPermissionGateEnabled: copilotChatPayload?.mcpPermissionGateEnabled,
      ...(directTurnTelemetrySurface ? {directTurnTelemetrySurface} : {}),
      ...modeControls,
    }),
    [copilotChatPayload, directTurnTelemetrySurface, modeControls],
  )

  const controller = {createTask: handleUserSubmit, createEnvironmentTask}

  return (
    <div className={clsx(styles.content, 'tmp-pt-3', className)}>
      {copilotChatPayload ? (
        <CopilotChatProvider
          threadId={null}
          refs={copilotChatPayload.refs || []}
          mode="immersive"
          ssoOrganizations={copilotChatPayload.ssoOrganizations || []}
          copilotChatPayload={copilotChatPayload}
        >
          {copilotFeatureFlags.chatSurfacesRewrite ? (
            <RewrittenNewSessionComposer
              controller={controller}
              initialInput={initialInput}
              draftSource={eventType}
              configuration={configuration}
              textAreaRef={textAreaRef}
            />
          ) : (
            <NewCopilotComposer
              controller={controller}
              initialInput={initialInput}
              draftSource={eventType}
              configuration={configuration}
              presentation={{
                textAreaRef,
                maxTextareaHeight,
                clearOnSubmit: true,
                disableWhileSubmitting: true,
              }}
            />
          )}
        </CopilotChatProvider>
      ) : (
        <SkeletonBox className={styles.skeleton} />
      )}
    </div>
  )
}
